import React, { useState, useEffect } from 'react';
import { adminGetMessages, adminMarkMessageRead, adminDeleteMessage } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Trash2, Eye, EyeOff } from 'lucide-react';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchData = async () => {
    try {
      const res = await adminGetMessages();
      setMessages(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleOpen = async (msg) => {
    setSelected(msg);
    if (msg.status === 'new') {
      try {
        await adminMarkMessageRead(msg.message_id);
        setMessages(prev => prev.map(m =>
          m.message_id === msg.message_id ? { ...m, status: 'read' } : m
        ));
      } catch (e) {
        console.error('Failed to mark message as read:', e);
      }
    }
  };

  const handleDelete = async (msgId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Nachricht wirklich löschen?')) return;
    try {
      await adminDeleteMessage(msgId);
      toast.success('Nachricht gelöscht');
      setMessages(prev => prev.filter(m => m.message_id !== msgId));
      if (selected?.message_id === msgId) setSelected(null);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const unreadCount = messages.filter(m => m.status === 'new').length;

  return (
    <div data-testid="admin-messages-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-heading font-bold uppercase">Nachrichten</h1>
          {unreadCount > 0 && (
            <Badge className="bg-primary text-white" data-testid="unread-messages-badge">
              {unreadCount} Neu
            </Badge>
          )}
        </div>
        <Badge variant="outline">{messages.length} Gesamt</Badge>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => (
            <Card key={i} className="card-surface animate-pulse">
              <CardContent className="p-6"><div className="h-16 bg-white/10 rounded" /></CardContent>
            </Card>
          ))}
        </div>
      ) : messages.length > 0 ? (
        <div className="space-y-4">
          {messages.map(msg => (
            <Card
              key={msg.message_id}
              className={`card-surface cursor-pointer hover:border-primary/30 transition-all ${msg.status === 'new' ? 'border-l-4 border-l-primary' : ''}`}
              onClick={() => handleOpen(msg)}
              data-testid={`message-card-${msg.message_id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start gap-3 flex-1">
                    {msg.status === 'new' ? (
                      <EyeOff className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    ) : (
                      <Eye className="w-5 h-5 text-text-secondary mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-bold ${msg.status === 'new' ? 'text-white' : 'text-text-secondary'}`}>
                          {msg.subject}
                        </h3>
                        {msg.type === 'sponsor_inquiry' && (
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">Sponsoren-Anfrage</Badge>
                        )}
                      </div>
                      <p className="text-sm text-text-secondary">{msg.name} &middot; {msg.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-text-secondary whitespace-nowrap">{formatDate(msg.created_at)}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300"
                      onClick={(e) => handleDelete(msg.message_id, e)}
                      data-testid={`delete-message-${msg.message_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-text-secondary line-clamp-2 pl-8">{msg.message}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-surface">
          <CardContent className="p-8 text-center">
            <Mail className="w-12 h-12 text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">Keine Nachrichten vorhanden.</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader>
            <DialogTitle>{selected?.subject}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-text-secondary">Name</p>
                  <p className="font-medium">{selected.name}</p>
                </div>
                <div>
                  <p className="text-text-secondary">E-Mail</p>
                  <a href={`mailto:${selected.email}`} className="text-accent hover:underline">{selected.email}</a>
                </div>
              </div>
              <div className="text-sm">
                <p className="text-text-secondary">Datum</p>
                <p>{formatDate(selected.created_at)}</p>
              </div>
              <div className="card-elevated p-4 whitespace-pre-wrap text-sm">{selected.message}</div>
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => handleDelete(selected.message_id)}
                data-testid="delete-message-dialog-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Nachricht löschen
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMessages;
